import { faUpload } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { log, processImageFileToSerialized, PushErrorNotification, styles } from "@repo/helpers";
import { SerializedFile } from "@repo/models";
import { Button, ButtonSize, ButtonTypes, TakePicture } from "@repo/ui";
import { ChangeEvent, useEffect, useRef, useState } from "react";

import style from "./style.module.scss";
import { Props } from "./types";

export default function AgentPhoto(props: Props) {
	const inputRef = useRef<HTMLInputElement>();
	const [agentPhoto, setAgentPhoto] = useState<SerializedFile>(null);
	const [useCamera, setUseCamera] = useState(false);
	const [initialized, setInitialized] = useState(false);

	useEffect(() => {
		setInitialized(true);
	}, []);

	useEffect(() => {
		initialized && props.onUpload(agentPhoto);
	}, [agentPhoto]);

	function renderUploadedPhoto() {
		if (!agentPhoto)
			return (
				<img
					className={styles("img-thumbnail", style.agentPhoto)}
					src={props.value ?? "/Images/default-profile-pic.jpg"}
					alt="Agent"
				/>
			);

		return <img className={styles("img-thumbnail", style.agentPhoto)} src={agentPhoto.url} alt="Agent" />;
	}

	function renderCamera() {
		if (!useCamera) return null;

		return (
			<TakePicture
				id="agent-photo"
				label=""
				value={agentPhoto}
				changeHandler={photoTaken}
				onError={onError}
				width={250}
				height={300}
				sizeFactor={1}
				review
				isSelfie
				openAtStart
				fullWidth
				showLoading
			/>
		);
	}

	return (
		<div className={style.container}>
			{renderUploadedPhoto()}

			<div className="grid w-full grid-cols-1 gap-2 pt-2">
				<Button
					onClick={uploadPhoto}
					size={ButtonSize.medium}
					types={[ButtonTypes.Danger]}
					disabled={props.readOnly}
				>
					<FontAwesomeIcon icon={faUpload} /> Choose a file...
				</Button>
				<Button
					onClick={toggleCamera}
					size={ButtonSize.medium}
					types={[ButtonTypes.Primary]}
					disabled={props.readOnly}
				>
					{"Use Camera"}
				</Button>
			</div>

			<input type="file" accept="image/*" ref={inputRef} onChange={fileUploaded} />

			{renderCamera()}
		</div>
	);

	function photoTaken(value: SerializedFile) {
		setAgentPhoto(value);
		setUseCamera(false);
	}

	function onError(error: string) {
		log(`Error while using camera: ${error}`, "error");
	}

	async function fileUploaded(e: ChangeEvent<HTMLInputElement>) {
		const file = e.target.files?.[0];
		if (!file) return;
		if (!file.type?.startsWith("image/")) {
			PushErrorNotification("Please select an image file.");
			return;
		}

		try {
			const compressed = await processImageFileToSerialized(file);
			setAgentPhoto(compressed);
		} catch (ex) {
			log(`Error compressing image: ${ex?.message ?? ex}`, "error");
			PushErrorNotification("Failed to process image. Please try a different file.");
		}
	}

	function uploadPhoto() {
		inputRef.current.click();
	}

	function toggleCamera() {
		setUseCamera(!useCamera);
	}
}
